import { clipPose, clipPoseAt } from '../world/life/citizens/citizenPose';
import type { CitizenAsset, CitizenClip } from '../world/life/citizens/citizenAsset';
import type { PeerPose } from './peers';
import { PLAYER } from '../core/config';
import { gesturePose } from './emotes';

const DIRECTIONS = [
  'Forward',
  'ForwardRight',
  'Right',
  'BackwardRight',
  'Backward',
  'BackwardLeft',
  'Left',
  'ForwardLeft',
];
const BLEND_SECONDS = 0.12;
/* A visitor going one of the eight ways keeps to its clip until it is this far past where the
   next begins, and one running or walking until its pace is this share of a walk: a pace or a
   direction that wavers about either would otherwise change its clip in every frame. */
const KEEP = { turn: (8 * Math.PI) / 180, running: 1.4, walking: 1.1, either: 1.25 } as const;

/** The way and the gait a visitor was last drawn going, for it to keep to (KEEP). */
export interface Going {
  octant: number | null;
  running: boolean;
}

export function visitorMovementClip(
  asset: CitizenAsset,
  peer: Pick<PeerPose, 'character' | 'mode' | 'yaw' | 'velocity'>,
  candle = false,
  going?: Going
): CitizenClip | null {
  const [vx, vz] = peer.velocity;
  const speed = Math.hypot(vx, vz);
  if (peer.mode !== 'walk' || speed < 0.02) {
    if (going) going.octant = null;
    return null;
  }
  const forward = -vx * Math.sin(peer.yaw) - vz * Math.cos(peer.yaw);
  const right = vx * Math.cos(peer.yaw) - vz * Math.sin(peer.yaw);
  const way = Math.atan2(right, forward);
  const kept = going?.octant ?? null;
  const off = kept === null ? Infinity : way - (kept * Math.PI) / 4;
  const octant =
    kept !== null && Math.abs(Math.atan2(Math.sin(off), Math.cos(off))) < Math.PI / 8 + KEEP.turn
      ? kept
      : (Math.round(way / (Math.PI / 4)) + 8) % 8;
  const share = !going || kept === null ? KEEP.either : going.running ? KEEP.walking : KEEP.running;
  const running = speed > PLAYER.walkSpeed * share;
  if (going) Object.assign(going, { octant, running });
  const prefix = `Visitor${candle ? 'Candle' : ''}${running ? 'Run' : 'Walk'}`;
  const name = `${prefix}${DIRECTIONS[octant]}${peer.character === 'female' ? 'Skirt' : ''}`;
  const clip = asset.clips[name];
  if (!clip?.strideM) throw new Error(`Missing visitor movement clip: ${name}`);
  return clip;
}

export function createVisitorLocomotion(asset: CitizenAsset, idle: CitizenClip) {
  let phase = 0,
    blend = 1,
    from = idle,
    to = idle;
  let beforeTime: number | null = null,
    beforeWalked: number | null = null;
  const going: Going = { octant: null, running: false };
  return {
    diagnostics(): { clip: string; phase: number; blend: number } {
      const name = Object.entries(asset.clips).find(([, clip]) => clip === to)?.[0];
      if (!name) throw new Error('Visitor animation is outside the citizen asset');
      return { clip: name, phase, blend };
    },
    sample(
      peer: PeerPose,
      seconds: number,
      candle = false
    ): { from: number; to: number; mix: number } {
      const dt = beforeTime === null ? 0 : Math.min(0.1, Math.max(0, seconds - beforeTime));
      const distance = beforeWalked === null ? 0 : Math.max(0, peer.walked - beforeWalked);
      beforeTime = seconds;
      beforeWalked = peer.walked;
      const next =
        visitorMovementClip(asset, peer, candle, going) ??
        (candle ? asset.clips['VisitorCandleIdle'] : idle);
      if (!next) throw new Error('Missing visitor candle idle clip');
      if (next !== to) {
        from = blend < 0.5 ? from : to;
        to = next;
        blend = 0;
      }
      if (next.strideM && distance < 2) phase = (phase + distance / next.strideM) % 1;
      blend = Math.min(1, blend + dt / BLEND_SECONDS);
      const pose = (clip: CitizenClip): number =>
        clip.strideM ? clipPose(clip, phase) : clipPoseAt(clip, seconds);
      return { from: pose(from), to: pose(to), mix: blend * blend * (3 - 2 * blend) };
    },
  };
}

export interface VisitorMovementDiagnostics {
  readonly id: string;
  readonly character: PeerPose['character'];
  readonly clip: string;
  readonly phase: number;
  readonly blend: number;
}

interface VisitorMotion {
  character: PeerPose['character'];
  movement: ReturnType<typeof createVisitorLocomotion>;
  gesture: ReturnType<typeof gesturePose>;
}

export function createVisitorMotionStore(asset: CitizenAsset) {
  const states = new Map<string, VisitorMotion>();
  return {
    begin(peers: readonly PeerPose[]): void {
      const present = new Set(peers.map((peer) => peer.id));
      for (const id of states.keys()) if (!present.has(id)) states.delete(id);
    },
    sample(
      peer: PeerPose,
      idle: CitizenClip,
      seconds: number,
      candle = false,
      epochMs = Date.now()
    ) {
      let acting = states.get(peer.id);
      if (!acting || acting.character !== peer.character) {
        acting = {
          character: peer.character,
          movement: createVisitorLocomotion(asset, idle),
          gesture: null,
        };
        states.set(peer.id, acting);
      }
      const motion = acting.movement.sample(peer, seconds, candle);
      acting.gesture =
        peer.mode === 'walk' ? gesturePose(asset, peer.emote, epochMs, candle) : null;
      const gesture = acting.gesture;
      return gesture
        ? { from: motion.mix < 0.5 ? motion.from : motion.to, to: gesture.row, mix: gesture.mix }
        : motion;
    },
    diagnostics(): readonly VisitorMovementDiagnostics[] {
      return [...states].map(([id, state]) => ({
        id,
        character: state.character,
        ...state.movement.diagnostics(),
        ...(state.gesture
          ? { clip: state.gesture.name, phase: state.gesture.phase, blend: state.gesture.mix }
          : {}),
      }));
    },
    clear(): void {
      states.clear();
    },
  };
}
